import { useEffect, useState } from "react";
import { MessageSquare, Inbox, Star, Users, Receipt, ShieldCheck } from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Cell } from "recharts";
import { api } from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Card, Loader } from "@/components/portal/ui";
import { StatCard } from "@/components/portal/StatCard";
import { StatusBadge } from "@/components/portal/StatusBadge";

export function ProviderReport() {
  const { t, L } = useI18n();
  const [data, setData] = useState(null);
  useEffect(() => { api.get("/reports/me").then(({ data }) => setData(data)); }, []);
  if (!data) return <Loader />;

  const chart = (data.monthly || []).map((m) => ({ name: m.month.slice(5), count: m.count }));

  return (
    <div className="kw-fade space-y-6" data-testid="provider-report">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard testid="report-card-inquiries" label={t("rep.inquiries")} value={data.inquiries_total} icon={Inbox} accent />
        <StatCard testid="report-card-open" label={t("stat.open_inquiries")} value={data.inquiries_open} icon={MessageSquare} delay={60} />
        <StatCard testid="report-card-rating" label={t("stat.rating")} value={data.rating} icon={Star} delay={120} />
        <StatCard testid="report-card-openinv" label={t("rep.openInvoices")} value={data.open_invoices} icon={Receipt} delay={180} />
      </div>

      <Card className="p-5">
        <div className="text-sm font-semibold mb-4" style={{ color: "var(--text)" }}>{t("rep.monthly")} · {t("rep.inquiries")}</div>
        <div style={{ width: "100%", minWidth: 0, height: 240 }}>
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={chart}>
              <XAxis dataKey="name" tick={{ fontSize: 12, fill: "var(--text-muted)" }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 12, fill: "var(--text-muted)" }} />
              <Tooltip contentStyle={{ background: "var(--surface)", border: "1px solid var(--border)", borderRadius: 8, color: "var(--text)" }} />
              <Bar dataKey="count" radius={[4, 4, 0, 0]}>
                {chart.map((_, i) => <Cell key={i} fill="var(--accent)" />)}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <div className="grid sm:grid-cols-2 gap-4">
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><ShieldCheck className="h-4 w-4" style={{ color: "var(--accent)" }} /><span className="text-sm font-semibold" style={{ color: "var(--text)" }}>{t("rep.profileStatus")}</span></div>
          {data.profile_status ? <StatusBadge status={data.profile_status} /> : <span className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.none")}</span>}
        </Card>
        <Card className="p-5">
          <div className="flex items-center gap-2 mb-3"><Users className="h-4 w-4" style={{ color: "var(--accent)" }} /><span className="text-sm font-semibold" style={{ color: "var(--text)" }}>{t("rep.package")}</span></div>
          {data.package ? (
            <div className="flex items-center gap-2">
              <span className="font-heading font-bold" style={{ color: "var(--text)" }}>{L(data.package.name)}</span>
              <span className="text-[10px] uppercase tracking-widest px-2 py-1 rounded-full" style={{ background: "var(--surface-alt)", color: "var(--accent)" }}>{data.package.tier}</span>
            </div>
          ) : <span className="text-sm" style={{ color: "var(--text-muted)" }}>{t("common.none")}</span>}
        </Card>
      </div>
    </div>
  );
}
